import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { createCopy, defaultLocale, type CopyKey, type Locale } from '@modelcast/copy';

interface CopyContextValue {
  locale: Locale;
  t: ReturnType<typeof createCopy>['t'];
}

const CopyContext = createContext<CopyContextValue>(createCopy(defaultLocale));

function getBrowserLocale() {
  if (typeof navigator === 'undefined') return defaultLocale;
  return navigator.languages?.[0] ?? navigator.language ?? defaultLocale;
}

export function CopyProvider({
  children,
  locale,
}: {
  children: ReactNode;
  locale?: string | null;
}) {
  const value = useMemo(() => createCopy(locale ?? getBrowserLocale()), [locale]);

  return (
    <CopyContext.Provider value={value}>
      {children}
    </CopyContext.Provider>
  );
}

export function useCopy() {
  return useContext(CopyContext);
}

export function useCopyText(key: CopyKey, params?: Parameters<CopyContextValue['t']>[1]) {
  const { t } = useCopy();
  return t(key, params);
}
